import React from 'react'
import { CAPABILITY } from '@trustero/trustero-api-web/lib/authz/permissions_pb'
import isFunction from 'lodash/isFunction'
import { useModalState } from 'src/Modal/ModalStateContext'
import {
  ResourceWithAuthz,
  useHasAnyCapability,
  useHasRequiredCapabilities,
} from 'src/app/AppAuth/AppAuth.hooks'
import { Spinner } from 'src/Throbber'
import {
  StandardButtonSize,
  StandardButtonVariant,
} from '../Reusable/Buttons/StandardButton'
import {
  Tooltip,
  TooltipOverlayType,
  TooltipPositions,
} from '../Reusable/Tooltip/Tooltip'
import { ModalFormId } from './ModalForm'
import {
  ModalButton,
  StyledModalButton,
  StyledToggleDiv,
} from './ModalForm.styles'

type StandardOpenModalButtonProps = {
  modalId: ModalFormId
  text: string
  variant: StandardButtonVariant
  size: StandardButtonSize
  isHidden: boolean
  isDisabled: boolean
  onClick?: () => void
  children?: React.ReactNode
  testId?: string
  disabledReason?: string
  tooltipValue?: string
  requiredCapabilities?: CAPABILITY[]
  // Holding any one of these authorizes the action, mirroring an rpc's any_of.
  anyOfCapabilities?: CAPABILITY[]
  resource?: ResourceWithAuthz
  // Still resolving which modal this should open. Shows a spinner and holds the
  // click, deliberately without disabledReason — the button is pending, not denied.
  isLoading?: boolean
}

export const StandardOpenModalButton = ({
  modalId,
  text,
  variant,
  size,
  isHidden,
  isDisabled,
  onClick,
  children,
  testId,
  disabledReason,
  tooltipValue,
  requiredCapabilities,
  anyOfCapabilities,
  resource,
  isLoading = false,
}: StandardOpenModalButtonProps): JSX.Element => {
  const hasCapabilities = useHasRequiredCapabilities(
    requiredCapabilities,
    resource,
  )
  const hasAnyCapability = useHasAnyCapability(anyOfCapabilities, resource)
  // Each half is vacuously true when its prop is unset, so a button that names
  // neither is ungated - which is the point of dropping the legacy default.
  const isAuthorized = hasCapabilities && hasAnyCapability

  let tooltipText = isDisabled ? disabledReason || tooltipValue : tooltipValue
  let isButtonDisabled = isDisabled
  if (!isDisabled && !isAuthorized) {
    tooltipText = 'You do not have permission to perform this action.'
    isButtonDisabled = true
  }
  if (isLoading) {
    // A pending button must not accuse the user of lacking permission.
    tooltipText = undefined
    isButtonDisabled = true
  }

  const { openModal } = useModalState()
  const onClickFunction = (e: React.MouseEvent) => {
    e.preventDefault()
    isFunction(onClick) && onClick()
    openModal(modalId)
  }

  return (
    <>
      <Tooltip
        id={text}
        tooltipBody={tooltipText}
        disabled={!tooltipText}
        overlayType={TooltipOverlayType.popover}
        placement={TooltipPositions.top}
      >
        <div>
          <StyledModalButton
            type="button"
            variant={variant}
            buttonSize={size}
            hidden={isHidden}
            disabled={isButtonDisabled}
            onClick={onClickFunction}
            data-testid={testId}
          >
            {isLoading ? <Spinner size="s" color="busy" /> : children || text}
          </StyledModalButton>
        </div>
      </Tooltip>
    </>
  )
}

StandardOpenModalButton.defaultProps = {
  text: 'Continue',
  variant: StandardButtonVariant.PRIMARY,
  size: StandardButtonSize.MEDIUM,
  isHidden: false,
  isDisabled: false,
}

type LargeOpenModalButtonProps = {
  modalId: ModalFormId
  children: React.ReactNode
  onClick?: () => void
  testId?: string
}

export const LargeOpenModalButton = ({
  modalId,
  children,
  onClick,
  testId,
}: LargeOpenModalButtonProps): JSX.Element => {
  const { openModal } = useModalState()
  const onClickFunction = (e: React.MouseEvent) => {
    e.preventDefault()
    isFunction(onClick) && onClick()
    openModal(modalId)
  }

  return (
    <ModalButton data-testid={testId} onClick={onClickFunction}>
      {children}
    </ModalButton>
  )
}

type OpenModalWrapperProps = {
  modalFormId: ModalFormId
  children: React.ReactNode
  onClick?: () => void
}

export const OpenModalWrapper = ({
  modalFormId,
  children,
  onClick,
}: OpenModalWrapperProps): JSX.Element => {
  const { openModal } = useModalState()

  const onClickFunction = (e: React.MouseEvent) => {
    e.preventDefault()
    isFunction(onClick) && onClick()
    openModal(modalFormId)
  }
  return <StyledToggleDiv onClick={onClickFunction}>{children}</StyledToggleDiv>
}
